import type { JointKey } from '../types';
import { buildJointMapFromFieldNames } from '../utils/jointMapping';
import tableHeadings from './table_heading';

export type RobotModelConfig = {
  id: string;
  label: string;
  urdfPath: string;
  meshRoot: string;
  /** MuJoCo 模型路径（前端静态资源路径，用于 3D 预览） */
  mjModelPath?: string;
  /** MuJoCo XML 模型路径（后端使用，相对于项目根目录） */
  xmlPath?: string;
  /** table_heading.ts 中的 key，用于获取 CSV 字段名列表 */
  headingKey?: string;
  defaultScale?: number;
  defaultCameraPosition?: [number, number, number];
  /**
   * 修正模型导入后的默认朝向，解决不同资产坐标系不一致的问题。
   * 正值代表绕 X/Y/Z 轴逆时针旋转。
   */
  defaultRotationX?: number;
  defaultRotationY?: number;
  defaultRotationZ?: number;
  /**
   * UI 中标准化的关节 key → URDF 实际 joint name 的映射。
   * 由 table_heading.json 中该模型的 CSV 字段名列表动态构建。
   * CSV 字段名即为 URDF 关节名 —— URDFLoader 会按此表找到对应 joint
   * 并通过 setJointValue(rad) 驱动旋转。
   */
  jointMap: Partial<Record<JointKey, string>>;
  /** MuJoCo 模型目录名（public/models/ 下），如 "booster_k1"。未配置则跳过 MuJoCo 物理渲染 */
  mujocoModelDir?: string;
  /** 机器人模型 Y 轴（高度）偏移量，正值抬高机器人。用于修正不同机型 URDF 脚底与地板的间距 */
  robotYOffset?: number;
};

// ===== table_heading.json 的 key → 模型 id 映射 =====
// 仅配置已加入 ROBOT_MODELS 的模型即可，未配置的模型回退到空映射
const HEADING_KEY_TO_MODEL_ID: Record<string, string> = {
  booster_k1: 'booster-k1',
  unitree_g1: 'unitree-g1',
  engineai_pm01: 'engineai-pm01',
  limx_oli: 'limx-oli',
};

/** 根据 table_heading.json 中该模型的字段名列表，动态构建 jointMap */
function resolveJointMap(modelId: string): Partial<Record<JointKey, string>> {
  const jsonKey = Object.keys(HEADING_KEY_TO_MODEL_ID).find(
    (k) => HEADING_KEY_TO_MODEL_ID[k] === modelId,
  );
  if (!jsonKey) {
    console.warn(`[robotModels] 模型 "${modelId}" 未在 table_heading.json 中找到映射`);
    return {};
  }

  const headings = tableHeadings[jsonKey];
  if (!headings || headings.length === 0) {
    console.warn(`[robotModels] 模型 "${modelId}" (key="${jsonKey}") 的字段列表为空`);
    return {};
  }

  const jointMap = buildJointMapFromFieldNames(headings);
  console.log(
    `[robotModels] "${modelId}" jointMap: ${Object.keys(jointMap).length} 个关节已匹配`,
  );
  return jointMap;
}

export const ROBOT_MODELS: RobotModelConfig[] = [
  {
    id: 'booster-k1',
    label: 'Booster / K1',
    urdfPath: '/descriptions/Booster/K1/K1.urdf',
    meshRoot: '/descriptions/Booster/K1/meshes',
    mjModelPath: '/models/booster_k1/K1.xml',
    xmlPath: 'public/models/booster_k1/K1.xml',
    headingKey: 'booster_k1',
    defaultScale: 1.0,
    defaultCameraPosition: [-8, 0.25, 3],
    defaultRotationX: Math.PI / 2,
    defaultRotationY: Math.PI,
    defaultRotationZ: 0,
    jointMap: resolveJointMap('booster-k1'),
    mujocoModelDir: 'booster_k1',
    robotYOffset: 0.1,
  },
  {
    id: 'engineai-pm01',
    label: 'EngineAI / PM01',
    urdfPath: '/descriptions/EngineAI/PM01/PM01.urdf',
    meshRoot: '/descriptions/EngineAI/PM01/meshes',
    headingKey: 'engineai_pm01',
    defaultScale: 1.12,
    defaultCameraPosition: [0, 0.25, 2.2],
    defaultRotationY: Math.PI,
    jointMap: resolveJointMap('engineai-pm01'),
  },
  {
    id: 'limx-oli',
    label: 'LimX / Oli',
    urdfPath: '/descriptions/LimX/Oli/Oli.urdf',
    meshRoot: '/descriptions/LimX/Oli/meshes',
    headingKey: 'limx_oli',
    defaultScale: 1.1,
    defaultCameraPosition: [0, 0.25, 2.2],
    defaultRotationY: Math.PI,
    jointMap: resolveJointMap('limx-oli'),
  },
  {
    id: 'unitree-g1',
    label: 'Unitree / G1',
    urdfPath: '/descriptions/Unitree/G1/G1.urdf',
    meshRoot: '/descriptions/Unitree/G1/meshes',
    mjModelPath: '/models/unitree_g1/G1.xml',
    xmlPath: 'public/models/unitree_g1/G1.xml',
    headingKey: 'unitree_g1',
    defaultScale: 1.18,
    defaultCameraPosition: [-2.2, 0.25, 0],
    defaultRotationX: Math.PI / 2,
    defaultRotationY: Math.PI,
    defaultRotationZ: Math.PI / 2,
    jointMap: resolveJointMap('unitree-g1'),
    mujocoModelDir: 'unitree_g1',
    robotYOffset: 0.1,
  },
];

export const DEFAULT_ROBOT_MODEL_ID = ROBOT_MODELS[0]?.id ?? 'booster-k1';

/** 接口使用的默认机器人型号（下划线格式，如 "booster_k1"），取自 table_heading 中首个已配置的模型 */
export const DEFAULT_API_ROBOT_MODEL: string =
  Object.keys(HEADING_KEY_TO_MODEL_ID).find(
    (k) => tableHeadings[k]?.length > 0,
  ) ?? 'booster_k1';

/** 获取接口支持的所有机器人型号列表（下划线格式） */
export function getApiRobotModels(): string[] {
  return Object.keys(HEADING_KEY_TO_MODEL_ID).filter(
    (k) => tableHeadings[k]?.length > 0,
  );
}

export function getRobotModelConfig(modelId?: string) {
  return ROBOT_MODELS.find((model) => model.id === modelId) ?? ROBOT_MODELS[0];
}

/** 根据任务返回的 robot_model 字符串（如 "booster_k1"）查找对应的模型配置 */
export function findModelConfigByRobotModel(robotModel: string): RobotModelConfig | undefined {
  if (!robotModel) return undefined;
  // 先按 id 精确匹配
  let config = ROBOT_MODELS.find((m) => m.id === robotModel);
  if (config) return config;

  // 再按 table_heading 的 key 映射
  for (const [headingKey, modelId] of Object.entries(HEADING_KEY_TO_MODEL_ID)) {
    if (headingKey === robotModel || modelId === robotModel) {
      return ROBOT_MODELS.find((m) => m.id === modelId);
    }
  }

  // 模糊匹配
  const lower = robotModel.toLowerCase();
  return ROBOT_MODELS.find(
    (m) =>
      m.id.toLowerCase().includes(lower) ||
      m.label.toLowerCase().includes(lower),
  );
}
